import { Injectable } from "@angular/core";
import { Observable, catchError, retry, throwError } from "rxjs";

// Models
import { OpportunityDto } from "../interfaces/opportunity.interface";
import { OpportunityLookup } from "../interfaces/opportunity-lookup.interface";

// Services
import {
	ApiEnum,
	HttpDataService,
} from "../../../_core/providers/http-data.service";

@Injectable({
	providedIn: "root",
})
export class OpportunityService {
	private readonly endpoint = "Oportunidad";

	constructor(private readonly httpDataService: HttpDataService) {}

	/**
	 * Lookup mínimo de oportunidades para selectores / binding (asociación a contrato).
	 * Devuelve solo cabecera; no expone el detalle completo.
	 */
	getLookup(): Observable<OpportunityLookup[]> {
		return this.httpDataService
			.get$<OpportunityLookup[]>(ApiEnum.API_ROOT, `/${this.endpoint}/Lookup`)
			.pipe(
				retry(2),
				catchError(
					this.httpDataService.handleError<OpportunityLookup[]>("getLookup"),
				),
			);
	}

	/**
	 * Get list of all opportunities
	 * @returns Observable of OpportunityDto array
	 */
	getOpportunitiesList(): Observable<OpportunityDto[]> {
		return this.httpDataService
			.get$<OpportunityDto[]>(ApiEnum.API_ROOT, `/${this.endpoint}/List`)
			.pipe(
				retry(3),
				catchError(
					this.httpDataService.handleError<OpportunityDto[]>(
						"getOpportunitiesList",
					),
				),
			);
	}

	/**
	 * Get opportunity by ID
	 * @param id Opportunity ID
	 * @returns Observable of OpportunityDto
	 */
	getOpportunityById(id: number): Observable<OpportunityDto> {
		return this.httpDataService
			.get$<OpportunityDto>(ApiEnum.API_ROOT, `/${this.endpoint}/${id}`)
			.pipe(
				retry(3),
				catchError(
					this.httpDataService.handleError<OpportunityDto>(
						"getOpportunityById",
					),
				),
			);
	}

	/**
	 * Create a new opportunity
	 * @param opportunity OpportunityDto object
	 * @returns Observable of OpportunityDto
	 */
	createOpportunity(opportunity: OpportunityDto): Observable<OpportunityDto> {
		return this.httpDataService
			.post$<OpportunityDto>(
				ApiEnum.API_ROOT,
				`/${this.endpoint}/Create`,
				opportunity,
			)
			.pipe(
				retry(3),
				catchError(
					this.httpDataService.handleError<OpportunityDto>(
						"createOpportunity",
					),
				),
			);
	}

	/**
	 * Update an existing opportunity
	 * @param opportunity OpportunityDto object
	 * @returns Observable of OpportunityDto
	 */
	updateOpportunity(opportunity: OpportunityDto): Observable<OpportunityDto> {
		return this.httpDataService
			.put$<OpportunityDto>(
				ApiEnum.API_ROOT,
				`/${this.endpoint}/Edit`,
				opportunity,
			)
			.pipe(
				retry(3),
				catchError(
					this.httpDataService.handleError<OpportunityDto>(
						"updateOpportunity",
					),
				),
			);
	}

	/**
	 * Delete an opportunity
	 * @param id Opportunity ID
	 * @returns Observable of void
	 */
	deleteOpportunity(id: number): Observable<void> {
		return this.httpDataService
			.delete$<void>(ApiEnum.API_ROOT, `/${this.endpoint}/Delete/${id}`)
			.pipe(
				retry(3),
				catchError(
					this.httpDataService.handleError<void>("deleteOpportunity"),
				),
			);
	}

	/**
	 * Get opportunities by contract ID
	 * @param contratoId Contract ID
	 * @returns Observable of OpportunityDto array
	 */
	getOpportunitiesByContrato(
		contratoId: number,
	): Observable<OpportunityDto[]> {
		return this.httpDataService
			.get$<
				OpportunityDto[]
			>(ApiEnum.API_ROOT, `/${this.endpoint}/FilterByContrato/${contratoId}`)
			.pipe(
				retry(3),
				catchError(
					this.httpDataService.handleError<OpportunityDto[]>(
						"getOpportunitiesByContrato",
					),
				),
			);
	}

	/**
	 * Get opportunities without assigned contract
	 * @returns Observable of OpportunityDto array
	 */
	getOpportunitiesSinContrato(): Observable<OpportunityDto[]> {
		return this.httpDataService
			.get$<
				OpportunityDto[]
			>(ApiEnum.API_ROOT, `/${this.endpoint}/ListSinContrato`)
			.pipe(
				retry(3),
				catchError(
					this.httpDataService.handleError<OpportunityDto[]>(
						"getOpportunitiesSinContrato",
					),
				),
			);
	}

	/**
	 * Assign a contract to an opportunity
	 * @param oportunidadId Opportunity ID
	 * @param contratoId Contract ID
	 */
	assignContrato(oportunidadId: number, contratoId: number): Observable<any> {
		return this.httpDataService
			.post$<any>(ApiEnum.API_ROOT, `/${this.endpoint}/AssignContrato`, {
				oportunidadId,
				contratoId,
			})
			.pipe(catchError((error) => throwError(() => error)));
	}

	/**
	 * Remove contract assignment from an opportunity
	 * @param oportunidadId Opportunity ID
	 */
	removeContrato(oportunidadId: number): Observable<any> {
		return this.httpDataService
			.delete$<any>(
				ApiEnum.API_ROOT,
				`/${this.endpoint}/RemoveContrato/${oportunidadId}`,
			)
			.pipe(catchError((error) => throwError(() => error)));
	}
}
